import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PersonalTable from './PersonalTable';
import ProfessionalTable from './ProfessionalTable';
import EditModal from './EditModal';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://www.giffypedia.com/php-api/';
const ITEMS_PER_PAGE = 10;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

// Add retry interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    
    if (!config || !response) {
      return Promise.reject(error);
    }
    
    config.retryCount = config.retryCount || 0;
    
    // Check if it's a retryable error
    const shouldRetry = response.status === 508 || response.status >= 500;
    
    if (shouldRetry && config.retryCount < MAX_RETRIES) {
      config.retryCount += 1;
      
      // Exponential backoff
      const delay = RETRY_DELAY * Math.pow(2, config.retryCount - 1);
      
      // Create new promise to handle retry
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Retry the request
      return apiClient(config);
    }
    
    return Promise.reject(error);
  }
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { isAdmin, isLoading: authLoading } = useAuth();
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(new AbortController());

  // Single source of truth for auth check
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/adminlogin', { replace: true });
    }
  }, [isAdmin, authLoading, navigate]);

  const [state, setState] = useState({
    activeTab: 'personal',
    personalData: [],
    professionalData: [],
    searchTerm: '',
    loading: true,
    editingRecord: null,
    notification: { show: false, message: '', type: '' },
    currentPage: 1,
    allColumns: [],
    personalVisibleColumns: ['id', 'name', 'email', 'phone', 'verified'],
    professionalVisibleColumns: ['id', 'companyname', 'email', 'phone', 'verified']
  });

  const showNotification = useCallback((message, type = 'info') => {
    setState(prev => ({
      ...prev,
      notification: { show: true, message, type }
    }));
    setTimeout(() => {
      setState(prev => ({
        ...prev,
        notification: { ...prev.notification, show: false }
      }));
    }, 3000);
  }, []);

  /** Fetch data for both personal & professional users */
  const fetchData = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAdmin) return;

    try {
      setState(prev => ({ ...prev, loading: true }));
      
      // Cancel any pending requests
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      const [personalRes, professionalRes] = await Promise.all([
        apiClient.get('CMSgetUsers.php?profile=personal', {
          signal: abortControllerRef.current.signal
        }),
        apiClient.get('CMSgetUsers.php?profile=professional', {
          signal: abortControllerRef.current.signal
        })
      ]);

      const personalData = Array.isArray(personalRes.data) ? personalRes.data : [];
      const professionalData = Array.isArray(professionalRes.data) ? professionalRes.data : [];

      const allColumns = [...new Set([
        ...personalData.flatMap(item => Object.keys(item || {})),
        ...professionalData.flatMap(item => Object.keys(item || {}))
      ])];

      setState(prev => ({
        ...prev,
        personalData,
        professionalData,
        allColumns,
        loading: false,
        currentPage: 1
      }));
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request canceled:', error.message);
        return;
      }
      
      // Handle authentication errors
      if (error.response?.status === 401) {
        navigate('/adminlogin');
        return;
      }
      
      console.error('API Error:', error);
      showNotification(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to fetch data',
        'error'
      );
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [isAdmin, navigate, showNotification]);

  /** Handle Delete */
  const handleDelete = async (id, profileType) => {
    if (!window.confirm(`Are you sure you want to delete this ${profileType} record?`)) return;

    try {
      const res = await apiClient.post(
        'CMSdeleteUser.php',
        { id, profile: profileType },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (res.data.success) {
        showNotification('Record deleted successfully', 'success');
        fetchData();
      } else {
        showNotification(res.data.error || 'Delete failed', 'error');
      }
    } catch (error) {
      console.error('Delete Error:', error);
      showNotification(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Delete failed',
        'error'
      );
    }
  };

  /** Handle Update (verify/edit) */
  const handleUpdate = async (updatedData) => {
    try {
      await apiClient.post(
        'CMSupdateUser.php',
        updatedData,
        { headers: { 'Content-Type': 'application/json' } }
      );
      showNotification('Record updated successfully', 'success');
      fetchData();
      setState(prev => ({ ...prev, editingRecord: null }));
    } catch (error) {
      console.error('Update Error:', error);
      showNotification(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Update failed',
        'error'
      );
    }
  };

  /** CSV Export */
  const exportToCSV = (data, filename) => {
    try {
      if (!data.length) {
        showNotification('No data to export', 'warning');
        return;
      }

      const visibleColumns = state.activeTab === 'personal'
        ? state.personalVisibleColumns
        : state.professionalVisibleColumns;

      const headers = visibleColumns.join(',');
      const rows = data.map(item =>
        visibleColumns.map(col =>
          `"${String(item[col] ?? '').replace(/"/g, '""')}"`
        ).join(',')
      ).join('\n');

      const blob = new Blob([`\uFEFF${headers}\n${rows}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export Error:', error);
      showNotification('Export failed: ' + error.message, 'error');
    }
  };

  // Debounced search handler
  const handleSearchChange = (value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setState(prev => ({
        ...prev,
        searchTerm: value,
        currentPage: 1
      }));
    }, 300);
  };

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (isAdmin) {
      fetchData();
    }
  }, [fetchData, isAdmin]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="page-body-container">
        <div className="mx-auto px-12 text-center py-20">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-4">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not admin (will redirect)
  if (!isAdmin) {
    return null;
  }

  /** Active tab columns */
  const visibleColumns = state.activeTab === 'personal'
    ? state.personalVisibleColumns
    : state.professionalVisibleColumns;

  /** Filter data */
  const filteredData = (state.activeTab === 'personal'
    ? state.personalData
    : state.professionalData
  ).filter(item =>
    visibleColumns.some(col =>
      String(item[col] ?? '').toLowerCase().includes(state.searchTerm.toLowerCase())
    )
  );

  /** Pagination */
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (state.currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedData = filteredData.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const goToPage = (pageNumber) => {
    setState(prev => ({ ...prev, currentPage: pageNumber }));
  };

  const nextPage = () => {
    setState(prev => ({
      ...prev,
      currentPage: Math.min(prev.currentPage + 1, totalPages)
    }));
  };

  const prevPage = () => {
    setState(prev => ({
      ...prev,
      currentPage: Math.max(prev.currentPage - 1, 1)
    }));
  };

  return (
    <div className="cmspage-body-container">
      <div className="cmspage-inner">
      <div className="mx-auto px-12">
        <h1 className="cmspage-title">Admin Dashboard</h1>

        {state.notification.show && (
          <div className={`fixed top-4 right-4 p-4 rounded shadow-lg z-50 ${
            state.notification.type === 'error' ? 'bg-red-500' :
            state.notification.type === 'success' ? 'bg-green-500' : 'bg-yellow-500'
          } text-white`}>
            {state.notification.message}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b mb-4">
          <button
            className={`py-2 px-4 font-medium ${state.activeTab === 'personal'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500'}`}
            onClick={() => setState(prev => ({
              ...prev,
              activeTab: 'personal',
              searchTerm: '',
              currentPage: 1
            }))}
          >
            Personal ({state.personalData.length})
          </button>
          <button
            className={`py-2 px-4 font-medium ${state.activeTab === 'professional'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500'}`}
            onClick={() => setState(prev => ({
              ...prev,
              activeTab: 'professional',
              searchTerm: '',
              currentPage: 1
            }))}
          >
            Professional ({state.professionalData.length})
          </button>
        </div>

        {/* Search & Export */}
        <div className="flex flex-wrap justify-between mb-4 gap-2">
          <input
            type="text"
            placeholder={`Search ${state.activeTab} records...`}
            className="border p-2 rounded w-full md:w-64"
            defaultValue={state.searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />

          <div className="flex gap-2">
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded whitespace-nowrap"
              onClick={() => exportToCSV(
                filteredData,
                `${state.activeTab}_profiles_${new Date().toISOString().split('T')[0]}.csv`
              )}
            >
              Export to CSV
            </button>
          </div>
        </div>

        {/* Table */}
        {state.loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-2">Loading data...</p>
          </div>
        ) : (
          <div>
            {paginatedData.length > 0 ? (
              state.activeTab === 'personal' ? (
                <PersonalTable
                  data={paginatedData}
                  columns={state.personalVisibleColumns}
                  onDelete={(id) => handleDelete(id, 'personal')}
                  onEdit={(record) => setState(prev => ({
                    ...prev,
                    editingRecord: record
                  }))}
                />
              ) : (
                <ProfessionalTable
                  data={paginatedData}
                  columns={state.professionalVisibleColumns}
                  onDelete={(id) => handleDelete(id, 'professional')}
                  onEdit={(record) => setState(prev => ({
                    ...prev,
                    editingRecord: record
                  }))}
                />
              )
            ) : (
              <div className="text-center py-8 border rounded bg-gray-50">
                <p className="text-gray-500">
                  {state.searchTerm
                    ? `No matching ${state.activeTab} records found`
                    : `No ${state.activeTab} records available`}
                </p>
                <button
                  onClick={fetchData}
                  className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Refresh Data
                </button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, totalItems)} of {totalItems} records
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={prevPage}
                    disabled={state.currentPage === 1}
                    className={`px-4 py-2 rounded ${state.currentPage === 1
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                  >
                    Previous
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (state.currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (state.currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = state.currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => goToPage(pageNum)}
                          className={`w-10 h-10 rounded ${state.currentPage === pageNum
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 hover:bg-gray-300'}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {totalPages > 5 && state.currentPage < totalPages - 2 && (
                      <span className="px-2">...</span>
                    )}

                    {totalPages > 5 && state.currentPage < totalPages - 2 && (
                      <button
                        onClick={() => goToPage(totalPages)}
                        className={`w-10 h-10 rounded ${state.currentPage === totalPages
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 hover:bg-gray-300'}`}
                      >
                        {totalPages}
                      </button>
                    )}
                  </div>

                  <button
                    onClick={nextPage}
                    disabled={state.currentPage === totalPages}
                    className={`px-4 py-2 rounded ${state.currentPage === totalPages
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Edit Modal */}
        {state.editingRecord && (
          <EditModal
            record={state.editingRecord}
            onClose={() => setState(prev => ({
              ...prev,
              editingRecord: null
            }))}
            onSave={handleUpdate}
          />
        )}
      </div>
    </div>
    </div>
  );
};

export default Dashboard;